'use client';

import { motion } from 'framer-motion';
import { type RefObject, useEffect, useRef, useState } from 'react';

const BAR_COUNT = 5;
// Frequency bin indices spread across low→mid range (out of 32 total bins at fftSize=64)
const BIN_INDICES = [2, 5, 9, 14, 20];
const MIN_H = 4;
const MAX_H = 24;
const LERP = 0.25;

function formatDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
}

export function VoiceVisualizer({
    analyserRef,
    isTranscribing,
    recordingDuration,
}: {
    analyserRef: RefObject<AnalyserNode | null>;
    isTranscribing: boolean;
    recordingDuration: number;
}) {
    const [bars, setBars] = useState<number[]>(Array(BAR_COUNT).fill(0));
    const smoothed = useRef<number[]>(Array(BAR_COUNT).fill(0));
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        if (!analyserRef.current || isTranscribing) {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            return;
        }

        const data = new Uint8Array(analyserRef.current.frequencyBinCount);

        const tick = () => {
            const node = analyserRef.current;
            if (!node) return; // node was nulled mid-session, stop the loop
            node.getByteFrequencyData(data);
            smoothed.current = smoothed.current.map((prev, i) => {
                const next = data[BIN_INDICES[i]] / 255;
                return prev + (next - prev) * LERP;
            });
            setBars([...smoothed.current]);
            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [analyserRef, isTranscribing]);

    return (
        <div className="flex flex-1 items-center gap-3 px-1 py-1">
            <div
                className="flex items-center gap-[4px]"
                role="img"
                aria-label={isTranscribing ? 'Transcribing audio' : 'Recording audio'}
            >
                {isTranscribing
                    ? Array.from({ length: BAR_COUNT }).map((_, i) => (
                          <motion.div
                              key={i}
                              className="w-[3px] rounded-full bg-default-500"
                              style={{ height: `${MAX_H / 2}px` }}
                              animate={{ scaleY: [0.3, 1, 0.3] }}
                              transition={{
                                  duration: 1,
                                  repeat: Infinity,
                                  delay: i * 0.12,
                                  ease: 'easeInOut',
                              }}
                          />
                      ))
                    : bars.map((level, i) => (
                          <div
                              key={i}
                              className="w-[3px] rounded-full bg-default-500"
                              style={{ height: `${MIN_H + level * (MAX_H - MIN_H)}px` }}
                          />
                      ))}
            </div>
            <span className="text-small text-default-400">
                {isTranscribing
                    ? 'Transcribing...'
                    : `Listening... ${formatDuration(recordingDuration)}`}
            </span>
        </div>
    );
}
